import React, { useRef, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLazyQuery, useQuery } from '@apollo/client';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Space, Button, Spin, Typography, message } from 'antd';
import { stringSorter } from 'utils';
import { CSSTransition } from 'react-transition-group';

import { ProTable } from 'components/layout';
import {
  renderProTableSearchProjectCategory,
  renderProTableSearchFaculty,
  renderProTableSearchDepartment,
  renderProTableSearchProjectStatus,
  renderProTableSearchFundingSource,
} from 'components/layout/ProTable';
import { GET_PROJECTS } from 'gql/projects';
import { BULK_DOWNLOAD_FILES } from 'gql/files';
import { TypeReducerContext } from '../BulkDownload';
import './ProjectTable.less';

const { Text } = Typography;
const bulkDownloadUrl = process.env.REACT_APP_BULK_DOWNLOAD_URL;

const ProjectsTable = ({ canViewAll, actionRef }) => {
  const [fileType] = useContext(TypeReducerContext);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const status = canViewAll ? 'ALL' : 'ACTIVE';
  const { data, refetch } = useQuery(GET_PROJECTS, {
    variables: { status },
  });
  const [, { loading, refetch: refetchDownload }] = useLazyQuery(
    BULK_DOWNLOAD_FILES,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        window.open(
          `${bulkDownloadUrl}?token=${data.bulkDownloadFiles}`,
          '_blank'
        );
      },
    }
  );
  const formRef = useRef();
  const nodeRef = useRef(null);
  const allProjectIds = data?.projects?.nodes?.map((project) => project.id);

  const onSelectChange = (newSelectedRowKeys) => {
    // console.log('selectedRowKeys changed: ', newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);
  };
  const columns = [
    {
      title: 'Project Category',
      key: 'projectCategory',
      sorter: (a, b) =>
        stringSorter(a.projectCategory.name, b.projectCategory.name),
      render: ({ projectCategory, id }) => (
        <Link to={`/projects/${id}/profile`}>{projectCategory.name}</Link>
      ),
      renderFormItem: renderProTableSearchProjectCategory,
    },
    {
      title: 'Project Title / Code / Work Prog.',
      key: 'keyword',
      hideInTable: true,
      colSize: 2,
    },
    {
      title: 'Faculty',
      key: 'faculty',
      hideInTable: true,
      renderFormItem: renderProTableSearchFaculty,
    },
    {
      title: 'Department',
      key: 'department',
      sorter: (a, b) =>
        stringSorter(
          a.profile?.department?.abbreviation,
          b.profile?.department?.abbreviation
        ),
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile ? profile.department.abbreviation : ' - '}
        </Link>
      ),
      renderFormItem: renderProTableSearchDepartment,
    },
    {
      title: 'Project Code',
      key: 'projectCode',
      hideInSearch: true,
      sorter: (a, b) =>
        stringSorter(a.profile?.projectCode, b.profile?.projectCode),
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile ? profile.projectCode : ' - '}
        </Link>
      ),
    },
    {
      title: 'Project Title',
      key: 'title',
      sorter: (a, b) => stringSorter(a.title, b.title),
      render: ({ title, id }) => (
        <Link to={`/projects/${id}/profile`}>{title}</Link>
      ),
      search: false,
    },
    {
      title: 'Work Prog.',
      key: 'workProgramme',
      hideInSearch: true,
      sorter: (a, b) =>
        stringSorter(a.profile?.workProgramme, b.profile?.workProgramme),
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile ? profile.workProgramme : ' - '}
        </Link>
      ),
    },
    {
      title: 'Project Duration',
      key: 'projectDuration',
      hideInSearch: true,
      sorter: (a, b) =>
        stringSorter(
          a.profile?.originalStartDate,
          b.profile?.originalStartDate
        ),
      render: ({ profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile
            ? `${moment(profile.originalStartDate).format(
                'DD/MM/YYYY'
              )} - ${moment(profile.originalCompletionDate).format(
                'DD/MM/YYYY'
              )}`
            : ' - '}
        </Link>
      ),
    },
    {
      title: 'Project Duration',
      key: 'searchProjectDuration',
      valueType: 'dateRange',
      hideInTable: true,
      search: {
        transform: (value) => {
          return {
            projectDuration: {
              startDate: value[0],
              endDate: value[1],
            },
          };
        },
      },
    },
    {
      title: 'Approved Funding Source',
      key: 'approvedFundingSource',
      hideInTable: true,
      renderFormItem: renderProTableSearchFundingSource,
    },
    {
      title: 'Additional Funding Source',
      key: 'additionalFundingSource',
      hideInTable: true,
      renderFormItem: renderProTableSearchFundingSource,
    },
    {
      title: 'Project Status',
      key: 'projectStatus',
      sorter: (a, b) =>
        stringSorter(a.projectStatus?.name, b.projectStatus?.name),
      render: ({ projectStatus, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {projectStatus ? projectStatus.name : ' - '}
        </Link>
      ),
      renderFormItem: renderProTableSearchProjectStatus,
    },
  ];
  const handleDownload = async (selectedRowKeys) => {
    if (fileType.length === 0) {
      return message.error('Please select file type');
    }
    await refetchDownload({
      input: {
        projectIds: selectedRowKeys,
        fileType,
      },
    });
  };

  return (
    <Spin spinning={loading} tip="Gathering files to download">
      <ProTable
        actionRef={actionRef}
        columns={columns}
        request={async (params = {}, _sort, _filter) => {
          try {
            const {
              department,
              faculty,
              keyword,
              projectCategory,
              projectDuration,
              projectStatus,
              approvedFundingSource,
              additionalFundingSource,
            } = params;
            const result = await refetch({
              status,
              projectStatus: projectStatus ? [projectStatus] : null,
              departments: department ? [department] : null,
              faculties: faculty ? [faculty] : null,
              keyword,
              projectCategories: projectCategory ? [projectCategory] : null,
              projectDuration,
              approvedFundingSource,
              additionalFundingSource,
            });
            return {
              data: result.data?.projects?.nodes,
              success: true,
            };
          } catch (error) {
            console.log(error);
            return {
              data: [],
              success: false,
            };
          }
        }}
        formRef={formRef}
        options={{
          density: false,
        }}
        search={{
          defaultCollapsed: true,
          span: {
            xs: 24,
            sm: 24,
            md: 12,
            lg: 12,
            xl: 8,
            xxl: 8,
          },
          labelWidth: 'auto',
        }}
        pagination={{
          defaultPageSize: 50,
        }}
        size="middle"
        rowKey="id"
        onReset={() => {
          if (formRef.current) {
            formRef.current.setFieldsValue({
              faculty: null,
              department: null,
              teamMember: null,
            });
            formRef.current.submit();
          }
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: onSelectChange,
        }}
        tableAlertRender={({ selectedRowKeys, onCleanSelected }) => (
          <>
            <CSSTransition
              classNames="alert-select-file-type"
              in={fileType.length === 0}
              timeout={200}
              nodeRef={nodeRef}
              unmountOnExit
            >
              <div
                style={{ display: 'block', marginBottom: '8px' }}
                ref={nodeRef}
              >
                <Text type="danger">Select files to download!</Text>
              </div>
            </CSSTransition>
            <Space size={24}>
              <span>
                Selected {selectedRowKeys.length} Projects
                <Button
                  type="link"
                  size="small"
                  style={{ marginLeft: 8 }}
                  onClick={onCleanSelected}
                >
                  Cancel selected
                </Button>
              </span>
            </Space>
          </>
        )}
        tableAlertOptionRender={({ selectedRowKeys }) => {
          return (
            <Space size={16}>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  handleDownload(selectedRowKeys);
                }}
              >
                Download files
              </Button>
            </Space>
          );
        }}
        toolBarRender={() => [
          <Button
            key="select-all"
            type="primary"
            onClick={() => setSelectedRowKeys(allProjectIds)}
          >
            Select all
          </Button>,
        ]}
      />
    </Spin>
  );
};

ProjectsTable.defaultProps = {
  canViewAll: false,
};

ProjectsTable.propTypes = {
  canViewAll: PropTypes.bool.isRequired,
};

export default ProjectsTable;
